import { AlertCircle, File02, Upload01, X } from "@untitledui/icons";
import { forwardRef, useEffect, useMemo, useState } from "react";
import { Image } from "@/components/ui/image";
import { useToast } from "@/providers/toast-provider";
import { cx } from "@/utils/cx";
import type { FileInputProps } from "../types";

const EXCEL_REGEX = /\.(xls|xlsx|csv)$/i;
const PDF_REGEX = /\.pdf$/i;
const FALLBACK_PDF_NAME = "Documento.pdf";

function getPathWithoutQuery(rawUrl: string): string {
	return rawUrl.split("?")[0] ?? rawUrl;
}

function getPdfFileNameFromUrl(rawUrl: string): string {
	try {
		const sanitized = getPathWithoutQuery(rawUrl);
		const lastSegment = sanitized.split("/").pop() ?? "";
		const decoded = decodeURIComponent(lastSegment).trim();
		if (decoded) {
			return decoded;
		}
		return FALLBACK_PDF_NAME;
	} catch {
		return FALLBACK_PDF_NAME;
	}
}

function isPdfFromPreviewValue(value: string): boolean {
	const normalized = value.trim().toLowerCase();
	if (!normalized) {
		return false;
	}
	if (normalized.startsWith("data:application/pdf")) {
		return true;
	}

	const withoutQuery = getPathWithoutQuery(normalized);
	return PDF_REGEX.test(withoutQuery);
}

export const FileInput = forwardRef<HTMLInputElement, FileInputProps>(
	(
		{
			name,
			label,
			mode = "image",
			maxSize = 2 * 1024 * 1024,
			onChange,
			initialPreview,
			error,
		},
		ref
	) => {
		const { showToast } = useToast();

		const [preview, setPreview] = useState<string | null>(
			initialPreview ?? null
		);
		const [fileName, setFileName] = useState<string | null>(null);
		const [fileUrl, setFileUrl] = useState<string | null>(null);

		const maxSizeInMB = useMemo(() => maxSize / 1024 / 1024, [maxSize]);

		const accept =
			mode === "image"
				? "image/*"
				: mode === "image_or_pdf"
					? "image/*,.pdf,application/pdf"
					: ".xls,.xlsx,.csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

		useEffect(() => {
			if (
				mode === "image_or_pdf" &&
				typeof initialPreview === "string" &&
				initialPreview.trim() !== "" &&
				isPdfFromPreviewValue(initialPreview)
			) {
				setPreview(null);
				setFileName(getPdfFileNameFromUrl(initialPreview));
				setFileUrl(initialPreview);
				return;
			}

			setPreview(initialPreview ?? null);
			setFileName(null);
			setFileUrl(null);
		}, [initialPreview, mode]);

		const handleFile = (selected: File | null) => {
			if (!selected) {
				return;
			}

			if (selected.size > maxSize) {
				showToast({
					title: "Arquivo muito grande",
					description: `O arquivo deve ter no máximo ${maxSizeInMB}MB.`,
					variant: "error",
				});
				return;
			}

			if (mode === "excel" && !EXCEL_REGEX.test(selected.name)) {
				showToast({
					title: "Formato inválido",
					description:
						"Envie apenas arquivos Excel (.xls/.xlsx) ou CSV (.csv).",
					variant: "error",
				});
				return;
			}

			if (mode === "image" && !selected.type.startsWith("image/")) {
				showToast({
					title: "Formato inválido",
					description: "Envie apenas arquivos de imagem (png, jpg, jpeg, etc).",
					variant: "error",
				});
				return;
			}

			if (
				mode === "image_or_pdf" &&
				!(
					selected.type.startsWith("image/") ||
					selected.type === "application/pdf" ||
					PDF_REGEX.test(selected.name)
				)
			) {
				showToast({
					title: "Formato inválido",
					description: "Envie apenas imagem ou PDF.",
					variant: "error",
				});
				return;
			}

			onChange?.(selected);

			if (mode === "image" || mode === "image_or_pdf") {
				const isImage = selected.type.startsWith("image/");

				if (!isImage) {
					setFileName(selected.name);
					setPreview(null);
					setFileUrl(null);
					return;
				}

				const reader = new FileReader();
				reader.onloadend = () => {
					setPreview(reader.result as string);
					setFileName(null);
					setFileUrl(null);
				};
				reader.readAsDataURL(selected);
			} else {
				setFileName(selected.name);
				setPreview(null);
				setFileUrl(null);
			}
		};

		const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
			handleFile(e.target.files?.[0] ?? null);
		};

		const handleDragOver = (e: React.DragEvent<HTMLButtonElement>) => {
			e.preventDefault();
		};

		const handleDrop = (e: React.DragEvent<HTMLButtonElement>) => {
			e.preventDefault();
			handleFile(e.dataTransfer.files?.[0] ?? null);
		};

		const openFilePicker = () => {
			document.getElementById(name)?.click();
		};

		const removeFile = () => {
			setPreview(null);
			setFileName(null);
			setFileUrl(null);
			onChange?.(null);
		};

		const hasImage =
			(mode === "image" || mode === "image_or_pdf") && Boolean(preview);
		const hasFileCard =
			(mode === "excel" || mode === "image_or_pdf") && Boolean(fileName);
		const hasContent = hasImage || hasFileCard;

		const renderContent = () => {
			if (hasImage && preview) {
				return (
					<div className="relative h-32 w-full">
						<Image
							alt="Preview"
							className="rounded-md border border-secondary_alt object-contain"
							fill
							priority
							sizes="(max-width: 768px) 100vw, 600px"
							src={preview}
						/>
					</div>
				);
			}

			if (hasFileCard && fileName) {
				const CardBody = (
					<div className="flex items-center gap-3 rounded-md border border-secondary_alt bg-white p-4 shadow-sm">
						<div className="flex h-10 w-10 items-center justify-center rounded-md bg-green-50">
							<File02 className="h-5 w-5 text-green-600" />
						</div>
						<div className="flex-1">
							<p className="font-medium text-gray-900 text-sm">{fileName}</p>
							<p className="text-gray-500 text-xs">
								{mode === "excel"
									? "Arquivo Excel selecionado"
									: "Arquivo PDF selecionado"}
							</p>
						</div>
					</div>
				);

				if (mode === "image_or_pdf" && fileUrl) {
					return (
						<a
							className="block"
							href={fileUrl}
							rel="noopener noreferrer"
							target="_blank"
						>
							{CardBody}
						</a>
					);
				}

				return <div>{CardBody}</div>;
			}

			return null;
		};

		return (
			<div className="flex w-full flex-col gap-1.5">
				{label ? <span className="font-medium text-sm">{label}</span> : null}

				<div className="relative flex w-full flex-col gap-1.5">
					{hasContent ? (
						<div className="relative w-full">
							{renderContent()}

							<button
								aria-label="Remover arquivo"
								className="absolute top-2 right-2 rounded-full bg-white p-1.5 shadow-md transition hover:bg-gray-100"
								onClick={removeFile}
								type="button"
							>
								<X className="h-4 w-4 text-red-500" />
							</button>
						</div>
					) : (
						<div className="w-full">
							<button
								className={cx(
									"flex w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed px-4 py-6 text-center text-sm transition",
									error
										? "border-error-primary bg-error-primary/5 hover:bg-error-primary/10"
										: "border-primary bg-primary text-muted-foreground hover:bg-primary_hover"
								)}
								onClick={openFilePicker}
								onDragOver={handleDragOver}
								onDrop={handleDrop}
								type="button"
							>
								<Upload01
									className={cx(
										"h-6 w-6 opacity-70",
										error ? "text-error-primary" : ""
									)}
								/>

								<div className="flex flex-col gap-1">
									<span
										className={cx(
											"font-medium",
											error ? "text-error-primary" : ""
										)}
									>
										Clique ou arraste seu arquivo aqui!
									</span>
									<span className="text-xs opacity-80">
										{mode === "image"
											? `Imagens até ${maxSizeInMB}MB`
											: mode === "image_or_pdf"
												? `Imagens ou PDF até ${maxSizeInMB}MB`
												: `Arquivos Excel (.xls, .xlsx, .csv) até ${maxSizeInMB}MB`}
									</span>
								</div>
							</button>

							<input
								accept={accept}
								className="hidden"
								id={name}
								name={name}
								onChange={handleChange}
								ref={ref}
								type="file"
							/>
						</div>
					)}
					{!!error && (
						<div className="flex items-center gap-2">
							<AlertCircle className="text-error-secondary" size={16} />
							<span className="text-error-primary text-sm">{error}</span>
						</div>
					)}
				</div>
			</div>
		);
	}
);

FileInput.displayName = "FileInput";
